<template>
  <div class="multi-warehouse-root">
    <div v-if="visible" class="dialog-overlay" @click="close">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>🏭 调整库存</h3>
          <button class="btn-close" @click="close">×</button>
        </div>

        <div class="dialog-body">
          <!-- 模式提示 -->
          <div class="mode-banner">
            <span v-if="isSingleWarehouse">当前模式：单仓库</span>
            <span v-else>当前模式：{{ warehouses.length }} 个仓库</span>
          </div>

          <!-- 仓库列表 -->
          <div class="warehouse-list">
            <!-- 店铺仓库（固定第 1 个） -->
            <WarehouseCard
              :warehouse="defaultWarehouse"
              :index="0"
              :is-default="true"
              :can-delete="false"
              @update="updateWarehouse"
            />

            <!-- 其他仓库 -->
            <WarehouseCard
              v-for="(warehouse, index) in additionalWarehouses"
              :key="warehouse.id"
              :warehouse="warehouse"
              :index="index + 1"
              :is-default="false"
              :can-delete="true"
              @update="updateWarehouse"
              @delete="deleteWarehouse"
            />
          </div>

          <!-- 总库存 -->
          <div class="total-stock-bar">
            <strong>总库存：{{ totalWarehouseStock }} {{ productUnit }}</strong>
          </div>

          <!-- 库存调整面板（库存有变化时显示） -->
          <div v-if="hasChanges" class="stock-adjust-hint">
            <div class="adjust-info">
              <span class="adjust-label">库存变化：</span>
              <span class="adjust-value" :class="totalDiff > 0 ? 'positive' : 'negative'">
                {{ totalDiff > 0 ? '+' : '' }}{{ totalDiff }} {{ productUnit }}
              </span>
            </div>

            <div class="adjust-form">
              <div class="form-row">
                <label>调整类型：</label>
                <select v-model="adjustType">
                  <option value="">请选择调整类型</option>
                  <option value="进货">进货</option>
                  <option value="出库">出库</option>
                  <option value="损耗">损耗</option>
                  <option value="盘点">盘点调整</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div class="form-row">
                <label>调整原因：</label>
                <input 
                  type="text" 
                  v-model="adjustReason" 
                  placeholder="请输入调整原因（必填）"
                />
              </div>
            </div>
          </div>

          <!-- 添加仓库按钮 -->
          <button class="btn-add-warehouse" @click="addWarehouse">
            + 新增仓库
          </button>
        </div>

        <div class="dialog-footer">
          <button class="btn-cancel" @click="close">取消</button>
          <button class="btn-save" :disabled="saving" @click="handleSave">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Toast 提示 -->
    <div class="toast" v-if="showToast">{{ toastMessage }}</div>
  </div>
</template>

<script>
import api from '@/services/api';
import WarehouseCard from './WarehouseCard.vue';

export default {
  name: 'MultiWarehouseDialog',
  
  components: {
    WarehouseCard
  },
  
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    productId: {
      type: Number,
      required: true
    },
    productName: {
      type: String,
      default: ''
    },
    wholesalerId: {
      type: Number,
      required: true
    },
    currentStock: {
      type: Number,
      default: 0
    },
    shopStockQuantity: {
      type: Number,
      default: null
    },
    productUnit: {
      type: String,
      default: '件'
    },
    hasMultiWarehouse: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['update:visible', 'saved'],
  
  data() {
    return {
      isSingleWarehouse: true,
      originalTotalStock: 0,
      originalWarehouses: [],
      defaultWarehouse: {
        id: 'default',
        warehouseId: null,
        warehouseName: '店铺仓库（默认）',
        stockQuantity: 0,
        isExpanded: true
      },
      additionalWarehouses: [],
      nextId: 1,
      showToast: false,
      toastMessage: '',
      saving: false,
      // 库存调整
      adjustType: '',
      adjustReason: ''
    };
  },
  
  computed: {
    warehouses() {
      return [this.defaultWarehouse, ...this.additionalWarehouses];
    },
    
    totalWarehouseStock() {
      return this.warehouses.reduce((sum, w) => sum + (Number(w.stockQuantity) || 0), 0);
    },
    
    calculatedTotal() {
      return this.warehouses.reduce((sum, w) => sum + (Number(w.stockQuantity) || 0), 0);
    },

    hasChanges() {
      if (this.originalWarehouses.length === 0) return false;
      
      const currentTotal = this.totalWarehouseStock;
      return currentTotal !== this.originalTotalStock;
    },
    
    // 库存总变化量
    totalDiff() {
      return this.calculatedTotal - this.originalTotalStock;
    }
  },
  
  mounted() {
    // 组件挂载时立即加载数据（但需要 productId 有效）
    if (this.productId && !isNaN(this.productId)) {
      this.loadWarehouseStocks();
    }
  },
  
  watch: {
    visible: {
      handler(val) {
        if (val && this.productId && !isNaN(this.productId)) {
          this.loadWarehouseStocks();
        }
      },
      immediate: false
    },
    productId: {
      handler(val) {
        if (val && !isNaN(val) && this.visible) {
          this.loadWarehouseStocks();
        }
      }
    }
  },
  
  methods: {
    // 加载仓库库存数据
    async loadWarehouseStocks() {
      try {
        console.log('🏭 加载仓库库存数据, productId:', this.productId);
        const product = await api.get(`/product/${this.productId}`);
        
        // 1. 获取所有仓库列表（拦截器已解包，直接返回数组）
        const warehouseListRes = await api.get('/warehouse/list', { 
          params: { wholesalerId: this.wholesalerId || 1 } 
        });
        const allWarehouses = Array.isArray(warehouseListRes) ? warehouseListRes : [];
        console.log('📦 所有仓库:', allWarehouses);
        
        // 2. 获取商品的仓库库存（拦截器已解包，直接返回数组）
        const stockRes = await api.get(`/warehouse/product/${this.productId}/stocks`);
        const stocks = Array.isArray(stockRes) ? stockRes : [];
        console.log('📦 商品库存:', stocks);
        console.log('📦 商品库存:', stocks);

        const shopStock = stocks.find(s => s.warehouseId == null);
        // ⭐ 优先用 product.shopStockQuantity（商品表为准），仓库表可能不同步
        const shopQty = product.shopStockQuantity != null ? Number(product.shopStockQuantity) : (shopStock?.stockQuantity != null ? Number(shopStock.stockQuantity) : 0);
        this.defaultWarehouse.stockQuantity = shopQty;
        this.defaultWarehouse.isExpanded = true;
        console.log('🏪 店铺仓库库存:', this.defaultWarehouse.stockQuantity);
        
        // 4. 加载所有分仓库，从中间表匹配库存
        console.log('🔍 仓库原始数据:', JSON.stringify(allWarehouses, null, 2));
        
        this.additionalWarehouses = allWarehouses.map(wh => {
          const stock = stocks.find(s => s.warehouseId === wh.id);
          const name = wh.warehouseName || wh.name || wh.warehouse_name || '未命名仓库';
          return {
            id: stock ? stock.id : null,
            warehouseId: wh.id,
            name: name,
            warehouseName: name,
            stockQuantity: stock ? Number(stock.stockQuantity) : 0,
            isExpanded: true
          };
        });
        
        console.log('📦 分仓库数量:', this.additionalWarehouses.length);
        this.additionalWarehouses.forEach((w, i) => {
          console.log(`  [${i}] ${w.warehouseName}: ${w.stockQuantity}`);
        });
        
        // 保存原始数据用于对比变化
        this.originalWarehouses = this.warehouses.map(w => ({...w}));
        this.originalTotalStock = this.totalWarehouseStock;
        
        this.isSingleWarehouse = this.additionalWarehouses.length === 0;
        
        console.log('✅ 仓库数据加载成功');
        console.log('   总仓库数:', this.warehouses.length);
        console.log('   原始总库存:', this.originalTotalStock);
      } catch (error) {
        console.error('❌ 加载仓库库存失败:', error);
        this.additionalWarehouses = [];
      }
    },
    
    close() {
      this.$emit('update:visible', false);
    },

    addWarehouse() {
      const newWarehouse = {
        id: Date.now() + this.nextId++,
        warehouseId: null,
        warehouseName: '',
        stockQuantity: 0,
        isExpanded: true
      };
      this.additionalWarehouses.push(newWarehouse);
      this.isSingleWarehouse = false;
    },

    deleteWarehouse(id) {
      this.additionalWarehouses = this.additionalWarehouses.filter(w => w.id !== id);
      
      if (this.additionalWarehouses.length === 0) {
        this.isSingleWarehouse = true;
      }
    },

    updateWarehouse(id, data) {
      if (id === 'default') {
        Object.assign(this.defaultWarehouse, data);
      } else {
        const warehouse = this.additionalWarehouses.find(w => w.id === id);
        if (warehouse) {
          Object.assign(warehouse, data);
        }
      }
    },

    // 处理保存（直接移植 ProductDetail.vue 的 saveMultiWarehouse 逻辑）
    async handleSave() {
      if (this.saving) return;
      
      // 验证库存调整面板
      if (this.hasChanges) {
        if (!this.adjustType) {
          alert('⚠️ 请选择库存调整类型');
          return;
        }
        if (!this.adjustReason?.trim()) {
          alert('⚠️ 请填写库存调整原因');
          return;
        }
      }
      
      this.saving = true;
      
      try {
        // 1. 构造 warehouseStocks（包含店铺仓库和所有实际仓库）
        const warehouseStocks = [
          {
            warehouseId: null,
            warehouseName: '店铺仓库',
            stockQuantity: Number(this.defaultWarehouse.stockQuantity) || 0
          },
          ...this.additionalWarehouses.map(w => ({
            warehouseId: w.warehouseId || null,
            warehouseName: w.warehouseName,
            stockQuantity: Number(w.stockQuantity) || 0
          }))
        ];
        
        const totalStock = this.totalWarehouseStock;
        
        console.log('📦 仓库库存数据:', JSON.stringify(warehouseStocks, null, 2));
        console.log('📊 总库存:', totalStock);
        console.log('🏷️ 商品名称:', this.productName);
        
        // 构造保存数据
        const saveData = {
          totalStock: totalStock,
          hasMultiWarehouse: true,
          warehouseStocks: warehouseStocks
        };
        
        // ⭐ 如果有库存变化，添加库存流水记录
        if (this.hasChanges) {
          const stockFlowRecords = this.buildStockFlowRecords();
          if (stockFlowRecords.length > 0) {
            saveData.stockFlowRecords = stockFlowRecords;
            console.log('📝 库存流水记录:', JSON.stringify(stockFlowRecords, null, 2));
          }
        }
        
        console.log('📤 保存数据:', JSON.stringify(saveData, null, 2));
        
        // 调用后端 API 保存
        await api.put(`/product/${this.productId}`, saveData);
        
        this.showToastMsg('✅ 保存成功！');
        
        // 通知父组件刷新
        this.$emit('saved');
        
        // 延迟关闭，让用户看到提示
        setTimeout(() => {
          this.close();
        }, 1500);
      } catch (error) {
        console.error('❌ 保存失败:', error);
        this.showToastMsg('保存失败：' + (error.response?.data?.message || error.message));
      } finally {
        this.saving = false;
      }
    },
    
    // 构造库存流水记录
    buildStockFlowRecords() {
      const records = [];
      const adjustType = this.adjustType;
      const reason = this.adjustReason?.trim() || '库存调整';
      
      // 对比每个仓库的库存变化
      this.warehouses.forEach(currentWh => {
        const originalWh = this.originalWarehouses.find(w => w.warehouseId === currentWh.warehouseId);
        const beforeQty = originalWh?.stockQuantity || 0;
        const afterQty = currentWh.stockQuantity || 0;
        
        if (beforeQty !== afterQty) {
          records.push({
            productId: this.productId,
            wholesalerId: this.wholesalerId || 1,
            warehouseId: currentWh.warehouseId,
            warehouseName: currentWh.warehouseName,
            adjustType: adjustType === '进货' ? 'IN' : 'OUT',
            changeQuantity: afterQty - beforeQty,
            beforeQuantity: beforeQty,
            afterQuantity: afterQty,
            adjustTypeLabel: adjustType,
            reason: reason
          });
        }
      });
      
      return records;
    },
    
    // 保存库存流水
    async saveStockRecord(records) {
      for (const record of records) {
        try {
          await api.post('/inventory/record', record);
        } catch (error) {
          console.error('❌ 库存流水保存失败:', record, error);
        }
      }
    },
    
    showToastMsg(message) {
      this.toastMessage = message;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 2000);
    }
  }
};
</script>

<style scoped>
.multi-warehouse-root {
  position: relative;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.dialog-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
}

.btn-close:hover {
  background: #e5e7eb;
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.mode-banner {
  padding: 12px;
  background: #e0f2fe;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #0369a1;
  font-weight: 500;
}

.warehouse-list {
  margin-bottom: 16px;
}

.total-stock-bar {
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 16px;
  font-size: 16px;
  color: #15803d;
}

/* 库存调整面板 */
.stock-adjust-hint {
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 16px;
}

.adjust-info {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 500;
}

.adjust-label {
  color: #92400e;
  margin-right: 8px;
}

.adjust-value {
  font-size: 18px;
  font-weight: 600;
}

.adjust-value.positive {
  color: #059669;
}

.adjust-value.negative {
  color: #dc2626;
}

.adjust-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-row label {
  font-size: 14px;
  color: #92400e;
  white-space: nowrap;
  min-width: 80px;
}

.form-row select,
.form-row input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}

.form-row select:focus,
.form-row input:focus {
  outline: none;
  border-color: #d97706;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

.btn-add-warehouse {
  width: 100%;
  padding: 12px;
  background: #fff;
  border: 1px dashed #667eea;
  color: #667eea;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-warehouse:hover {
  background: #f5f3ff;
  border-color: #764ba2;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.btn-cancel {
  padding: 10px 20px;
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f9fafb;
}

.btn-save {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10002;
}
</style>
